import React from 'react';
import { Link } from 'react-router-dom';
import { Lock, Rocket, BookOpen, Users, ArrowRight, Calendar, Home } from 'lucide-react';
import MatrixBackground from './ui/MatrixBackground';

type GateType = 'bootcamp' | 'cursos' | 'mentorias';

interface AccessGateProps {
  type: GateType;
  userName?: string;
}

const CALENDLY_URL = 'https://calendly.com/lucianoagustinargolo/sesion_informativa';

const GATE_CONFIG: Record<GateType, {
  icon: React.ReactNode;
  title: string;
  subtitle: string;
  features: string[];
  ctaLabel: string;
  ctaLink: string;
  ctaExternal?: boolean;
  secondaryLabel: string;
  secondaryLink: string;
  secondaryExternal?: boolean;
  accentColor: string;
  accentBg: string;
  accentBorder: string;
}> = {
  bootcamp: {
    icon: <Rocket className="w-8 h-8" />,
    title: 'Bootcamp de Data Engineering',
    subtitle: '10 semanas intensivas para transformar tu carrera en datos.',
    features: [
      'SQL avanzado con Databricks',
      'Arquitectura Medallion y Delta Lake',
      'ETL, pipelines y automatización',
      'Proyecto final con datos reales',
      'Comunidad y soporte en vivo',
    ],
    ctaLabel: 'Agendá una sesión informativa',
    ctaLink: CALENDLY_URL,
    ctaExternal: true,
    secondaryLabel: 'Conocé más sobre el bootcamp',
    secondaryLink: '/Inicia',
    accentColor: 'text-emerald-400',
    accentBg: 'bg-emerald-500',
    accentBorder: 'border-emerald-500/30',
  },
  cursos: {
    icon: <BookOpen className="w-8 h-8" />,
    title: 'Cursos de Data Engineering',
    subtitle: 'Accedé a todo el contenido técnico de la plataforma.',
    features: [
      'Videos exclusivos semanales',
      'Ejercicios prácticos con soluciones',
      'Comunidad privada en Discord',
      'Plantillas y recursos descargables',
      'Q&A mensuales en vivo',
    ],
    ctaLabel: 'Agendá una sesión informativa',
    ctaLink: CALENDLY_URL,
    ctaExternal: true,
    secondaryLabel: 'Ver opciones de suscripción',
    secondaryLink: '/Inicia',
    accentColor: 'text-blue-400',
    accentBg: 'bg-blue-500',
    accentBorder: 'border-blue-500/30',
  },
  mentorias: {
    icon: <Users className="w-8 h-8" />,
    title: 'Mentorías 1:1',
    subtitle: 'Acompañamiento personalizado para tu transición a datos.',
    features: [
      'Sesiones 1:1 de 60 minutos',
      'Roadmap personalizado de carrera',
      'Revisión de CV y portfolio',
      'Preparación para entrevistas técnicas',
      'Acceso a plataforma de ejercicios',
    ],
    ctaLabel: 'Agendá una sesión diagnóstico',
    ctaLink: CALENDLY_URL,
    ctaExternal: true,
    secondaryLabel: 'Ver planes de mentoría',
    secondaryLink: '/mentorias',
    accentColor: 'text-purple-400',
    accentBg: 'bg-purple-500',
    accentBorder: 'border-purple-500/30',
  },
};

export default function AccessGate({ type, userName }: AccessGateProps) {
  const config = GATE_CONFIG[type];

  const handleCTA = () => {
    if (config.ctaExternal) {
      window.open(config.ctaLink, '_blank');
    }
  };

  const handleRefreshAccess = () => {
    window.location.reload();
  };

  return (
    <div className="relative min-h-screen flex items-center justify-center overflow-hidden bg-[#0a0f1a]">
      <MatrixBackground color="#00ff41" animated />

      <div className="relative z-10 w-full max-w-lg mx-4">
        <div className={`bg-[#0d1117]/95 backdrop-blur-xl rounded-2xl border ${config.accentBorder} shadow-2xl overflow-hidden`}>
          {/* Header */}
          <div className="px-8 pt-8 pb-6 text-center">
            <div className={`inline-flex items-center justify-center w-16 h-16 rounded-2xl ${config.accentBg}/10 border ${config.accentBorder} mb-5`}>
              <span className={config.accentColor}>{config.icon}</span>
            </div>

            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-amber-500/10 border border-amber-500/20 mb-4">
              <Lock className="w-3.5 h-3.5 text-amber-400" />
              <span className="text-xs font-medium text-amber-400">Contenido exclusivo</span>
            </div>

            <h1 className="text-2xl font-bold text-white mb-2">{config.title}</h1>
            <p className="text-gray-400 text-sm leading-relaxed">
              {userName ? `${userName}, e` : 'E'}ste contenido requiere acceso.{' '}
              {config.subtitle}
            </p>
          </div>

          {/* Features */}
          <div className="px-8 pb-6">
            <div className="bg-[#161b22] rounded-xl p-4 space-y-2.5">
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">Qué incluye</p>
              {config.features.map((feature, i) => (
                <div key={i} className="flex items-start gap-2.5">
                  <div className={`w-1.5 h-1.5 rounded-full ${config.accentBg} mt-1.5 shrink-0`} />
                  <span className="text-sm text-gray-300">{feature}</span>
                </div>
              ))}
            </div>
          </div>

          {/* CTAs */}
          <div className="px-8 pb-8 space-y-3">
            {config.ctaExternal ? (
              <button
                onClick={handleCTA}
                className={`w-full ${config.accentBg} hover:brightness-110 text-white font-semibold py-3.5 px-6 rounded-xl transition-all flex items-center justify-center gap-2 text-sm shadow-lg`}
              >
                <Calendar className="w-4 h-4" />
                {config.ctaLabel}
              </button>
            ) : (
              <Link
                to={config.ctaLink}
                className={`w-full ${config.accentBg} hover:brightness-110 text-white font-semibold py-3.5 px-6 rounded-xl transition-all flex items-center justify-center gap-2 text-sm shadow-lg`}
              >
                {config.ctaLabel}
                <ArrowRight className="w-4 h-4" />
              </Link>
            )}

            <Link
              to={config.secondaryLink}
              className="w-full block text-center py-3 text-sm font-medium text-gray-400 hover:text-white transition-colors rounded-xl border border-gray-700/50 hover:border-gray-600"
            >
              {config.secondaryLabel}
            </Link>

            <button
              onClick={handleRefreshAccess}
              className="w-full text-center py-2 text-xs text-gray-500 hover:text-emerald-400 transition-colors"
            >
              ¿Ya te dieron acceso? Tocá acá para verificar
            </button>

            <Link
              to="/"
              className="w-full flex items-center justify-center gap-1.5 pt-2 text-xs text-gray-500 hover:text-gray-400 transition-colors"
            >
              <Home className="w-3 h-3" />
              Volver al inicio
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
